import {
  Wallet,
  CheckCircle,
  Home,
  Shield,
  Building2,
  Briefcase,
  PiggyBank,
  Coins,
  SlidersHorizontal,
  Calendar,
  Percent,
  ArrowLeftRight,
  ExternalLink,
  MapPin,
  ListChecks,
} from "lucide-react";

/* Job-detail tour content, ported from the mobile "Job Detail" mockup. The
   mortgage-calculator steps carry `openSelector` so the engine expands the
   collapsed "Customize mortgage details" panel before highlighting the
   inputs. Steps whose target isn't present (e.g. sticky bar on desktop, or a
   section that hasn't loaded) auto-skip. */
const OPEN_CALC = '[data-tour="jd-calc-toggle"]';

// The sticky "Apply" bar only renders at ≤768px (CSS `.mobile-apply-bar`),
// so its tour step is mobile-only — on desktop it has no visible target.
const STICKY_BAR_MAX_WIDTH = 768;

export default function getJobDetailTourSteps() {
  const steps = [
    {
      cardSelector: '[data-tour="jd-bonus"]',
      targetSelector: '[data-tour="jd-bonus"]',
      icon: <Wallet />,
      title: "Sign-on bonus",
      text: "This role includes a sign-on bonus — one of the job's standout perks.",
    },
    {
      cardSelector: '[data-tour="jd-apply"]',
      targetSelector: '[data-tour="jd-apply"]',
      icon: <CheckCircle />,
      title: "Apply now",
      text: "Apply directly to this position in a couple of taps.",
    },
    {
      cardSelector: '[data-tour="jd-snapshot"]',
      targetSelector: '[data-tour="jd-snapshot"]',
      icon: <Home />,
      title: "City snapshot",
      text: "A quick read on the city where this job is based.",
    },
    {
      cardSelector: '[data-tour="jd-snapshot"]',
      targetSelector: '[data-tour="jd-crime"]',
      icon: <Shield />,
      title: "Crime",
      text: "The local crime grade for this city.",
    },
    {
      cardSelector: '[data-tour="jd-snapshot"]',
      targetSelector: '[data-tour="jd-hosp"]',
      icon: <Building2 />,
      title: "Hospitals",
      text: "How many hospitals are nearby.",
    },
    {
      cardSelector: '[data-tour="jd-hospitals"]',
      targetSelector: '[data-tour="jd-hospitals"]',
      icon: <Building2 />,
      title: "Hospitals in this city",
      text: "The hospitals in this city, closest first.",
    },
    {
      cardSelector: '[data-tour="jd-hospitals"]',
      targetSelector: '[data-tour="jd-hospital-item"]',
      icon: <ExternalLink />,
      title: "Open a hospital's site",
      text: "Tap a hospital to open its own careers website in a new tab.",
    },
    {
      cardSelector: '[data-tour="jd-jobs"]',
      targetSelector: '[data-tour="jd-jobs"]',
      icon: <Briefcase />,
      title: "Jobs for this city",
      text: "Browse other open roles available in this city.",
    },
    {
      cardSelector: '[data-tour="jd-salary"]',
      targetSelector: '[data-tour="jd-salary"]',
      icon: <Wallet />,
      title: "Salary & Housing",
      text: "Typical pay and housing costs across all roles here.",
    },
    {
      cardSelector: '[data-tour="jd-salary"]',
      targetSelector: '[data-tour="jd-leftover"]',
      icon: <PiggyBank />,
      title: "Left over each month",
      text: "What's left after tax and housing on the city average.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-afford"]',
      icon: <Home />,
      title: "Can you afford it?",
      text: "Runs the numbers on this job's actual posted pay.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-down"]',
      icon: <Coins />,
      title: "Down payment needed",
      text: "The upfront cash you'd need to buy a home here.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-calc-toggle"]',
      icon: <SlidersHorizontal />,
      title: "Mortgage details",
      text: "Open this to fine-tune the mortgage assumptions.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-price"]',
      openSelector: OPEN_CALC,
      icon: <Home />,
      title: "Home price",
      text: "Adjust the home price used in the estimate.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-dp"]',
      openSelector: OPEN_CALC,
      icon: <Coins />,
      title: "Down payment",
      text: "Slide to set your down-payment percentage.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-term"]',
      openSelector: OPEN_CALC,
      icon: <Calendar />,
      title: "Loan term",
      text: "Choose a 15- or 30-year mortgage.",
    },
    {
      cardSelector: '[data-tour="jd-afford"]',
      targetSelector: '[data-tour="jd-rate"]',
      openSelector: OPEN_CALC,
      icon: <Percent />,
      title: "Interest rate",
      text: "Enter the mortgage interest rate.",
    },
    {
      cardSelector: '[data-tour="jd-listings"]',
      targetSelector: '[data-tour="jd-mode"]',
      icon: <ArrowLeftRight />,
      title: "For Sale / For Rent",
      text: "Switch the listings view between buying and renting.",
    },
    {
      cardSelector: '[data-tour="jd-listings"]',
      targetSelector: '[data-tour="jd-redfin"]',
      icon: <ExternalLink />,
      title: "Redfin",
      text: "Browse live listings for this city on Redfin.",
    },
    {
      cardSelector: '[data-tour="jd-listings"]',
      targetSelector: '[data-tour="jd-zillow"]',
      icon: <ExternalLink />,
      title: "Zillow",
      text: "Browse live listings for this city on Zillow.",
    },
    {
      cardSelector: '[data-tour="jd-citycta"]',
      targetSelector: '[data-tour="jd-citycta"]',
      icon: <MapPin />,
      title: "More about this city",
      text: "Full city details — demographics, schools, climate and more.",
    },
    {
      cardSelector: '[data-tour="jd-similar"]',
      targetSelector: '[data-tour="jd-similar"]',
      icon: <Briefcase />,
      title: "Similar jobs",
      text: "Other openings in this city, ranked by take-home.",
    },
    {
      cardSelector: '[data-tour="jd-similar"]',
      targetSelector: '[data-tour="jd-similar0"]',
      icon: <ListChecks />,
      title: "Compare take-home",
      text: "Each card shows what's left after housing per month.",
    },
  ];

  // Mobile-only: the sticky Apply bar doesn't exist on desktop.
  const isMobile =
    typeof window !== "undefined" &&
    window.innerWidth <= STICKY_BAR_MAX_WIDTH;
  if (isMobile) {
    steps.push({
      cardSelector: '[data-tour="jd-sticky"]',
      targetSelector: '[data-tour="jd-sticky"]',
      icon: <CheckCircle />,
      title: "Apply anytime",
      text: "This bar keeps Apply within reach as you scroll.",
    });
  }

  return steps;
}
